import { type SupportedCountryCode } from '@zep-us/i18n';
import { type TFunction } from 'i18next';

import { type CategoryTree } from '@/shared/category/type';
import { type Namespace } from '@/types/translation';
import { getGradeNumber, isAllGrades } from '@/views/build/util';

import { type CategoryInfoInputType } from '../../category-info/types';
import { type CategoryNode } from '../../category-tree';
import { type PublisherInfo } from '../../publisher-category-selector/types';
import { type CategoryLabels } from '../types';

import {
  generateGradeLabel,
  generatePublisherLabel,
  generateSchoolTypeLabel,
  generateSemesterLabel,
  generateSubjectLabel,
} from './labelGenerators';

export interface GenerateCategoryLabelsParams {}

/**
 * 카테고리 정보로부터 모든 라벨을 생성하는 Pure Function
 *
 * Hook이 아닌 순수 함수로 구현하여 다양한 컨텍스트에서 재사용 가능합니다.
 * - useCategoryLabels: 반응형 hook에서 useMemo와 함께 사용
 * - useSyncQuizTitleWithCategory: 이벤트 핸들러에서 반환된 함수 호출
 *
 * @param params - 라벨 생성에 필요한 모든 파라미터
 * @returns 생성된 모든 카테고리 라벨
 *
 * @example
 * const labels = generateCategoryLabels({
 *   categoryValues,
 *   schoolInfoTree,
 *   subjectNodes,
 *   publishers,
 *   userCountryCode: 'KR',
 *   t: i18next.t,
 * });
 */
export const generateCategoryLabels = ({
  categoryValues,
  schoolInfoTree,
  subjectNodes,
  publishers,
  userCountryCode,
  t,
}: {
  /** 선택된 카테고리 정보 */
  categoryValues: CategoryInfoInputType;
  /** 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** 과목 트리를 CategoryNode로 변환한 배열 */
  subjectNodes: CategoryNode[];
  /** 출판사 목록 (이미 국가 코드로 필터링된 상태) */
  publishers?: PublisherInfo[];
  /** 사용자 국가 코드 */
  userCountryCode: SupportedCountryCode;

  t: TFunction<Namespace>;
}): CategoryLabels => {
  // 1. 학교급 label
  const schoolTypeNode = schoolInfoTree.children.find(
    node => node.categoryTreeId === categoryValues.schoolTypeId,
  );
  const schoolTypeLabel = schoolTypeNode
    ? generateSchoolTypeLabel(schoolTypeNode.name, t)
    : undefined;

  // 2. 학년 label
  const gradeLabel = generateGradeLabel(
    categoryValues.gradeId,
    userCountryCode,
    t,
  );

  const gradeNumber = getGradeNumber(categoryValues.gradeId);
  const isAllGradesSelected = isAllGrades(gradeNumber);

  // 3. 학기 label (전학년이면 숨김)
  const semesterLabel = isAllGradesSelected
    ? undefined
    : generateSemesterLabel(categoryValues.semesterId, userCountryCode, t);

  // 4. 과목 label (subSubject 우선, 없으면 subject)
  const subjectLabel = generateSubjectLabel({
    subjectId: categoryValues.subject,
    subSubjectId: categoryValues.subSubject,
    subjectNodes,
  });

  // 5. 출판사 label (전학년이면 숨김)
  const publisherLabel = isAllGradesSelected
    ? undefined
    : generatePublisherLabel({
        publisherId: categoryValues.publisher,
        customPublisher: categoryValues.customPublisher,
        publishers,
        userCountryCode,
        schoolTypeId: categoryValues.schoolTypeId,
        gradeId: categoryValues.gradeId,
      });

  return {
    schoolTypeLabel,
    gradeLabel,
    semesterLabel,
    subjectLabel,
    publisherLabel,
  };
};
